/** Dati del cruscotto (V06), la Home, come li restituisce l'API. */

import type { StatoGiocatore, StatoSportivo } from '@calcio/shared';
import { dataLocale, oraLocale } from '@calcio/shared';
import { segnaProssimo } from '../comuni/passi.js';
import type { Esito, StatoPartita } from '../partite/raggruppa.js';

export interface PartitaInBreve {
  id: string;
  dataOra: string;
  avversario: string;
  inCasa: boolean;
  competizione: string;
}

export interface GiocatoreInBreve {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
}

export interface Avvisi {
  /** Diffide e squalifiche nelle competizioni attive, una riga per competizione. */
  statiSportivi: (GiocatoreInBreve & { competizione: string; stato: StatoSportivo })[];
  nonDisponibili: (GiocatoreInBreve & { stato: Exclude<StatoGiocatore, 'disponibile'> })[];
  daChiudere: PartitaInBreve[];
  /** Null per chi non legge i voti. */
  senzaVoti: PartitaInBreve[] | null;
  incoerenze: (PartitaInBreve & { incoerenze: number })[];
  senzaAppello: { id: string; dataOra: string; luogo: string | null }[];
}

/** Passi guidati della squadra senza rosa, solo per chi la prepara. */
export interface Passi {
  impostazioni: boolean;
  staff: boolean;
}

export interface Cruscotto {
  senzaRosa: boolean;
  passi: Passi | null;
  inCorso:
    | (PartitaInBreve & {
        stato: StatoPartita;
        golFatti: number;
        golSubiti: number;
        cronometro: {
          stato: 'tra_periodi' | 'in_gioco' | 'sospesa' | 'terminata';
          etichetta: string;
        } | null;
      })
    | null;
  prossimo:
    | (PartitaInBreve & { tipo: 'partita'; convocati: number })
    | { tipo: 'allenamento'; id: string; dataOra: string; luogo: string | null; rosa: number }
    | null;
  /** Partite e allenamenti programmati nei prossimi sette giorni, il prossimo compreso. */
  agenda: (
    | (PartitaInBreve & { tipo: 'partita' })
    | { tipo: 'allenamento'; id: string; dataOra: string; luogo: string | null }
  )[];
  stagione: {
    giocate: number;
    vinte: number;
    pareggiate: number;
    perse: number;
    golFatti: number;
    golSubiti: number;
    /** Le ultime cinque, la piu recente per prima. */
    forma: Esito[];
    marcatori: { id: string; nome: string; cognome: string; gol: number }[];
  };
  /** Presenza: quota di presenti sulle presenze registrate, null senza appelli. */
  allenamenti: { svolti: number; presenza: number | null };
  ultimiRisultati: (PartitaInBreve & {
    golFatti: number;
    golSubiti: number;
    marcatori: { nome: string; cognome: string; etichetta: string }[];
  })[];
  avvisi: Avvisi;
  rosa: {
    tesserati: number;
    /** Assente per chi non legge i voti, null senza voti. */
    mediaVoto?: number | null;
  };
}

export function contaAvvisi(a: Avvisi): number {
  return (
    a.statiSportivi.length +
    a.nonDisponibili.length +
    a.daChiudere.length +
    (a.senzaVoti?.length ?? 0) +
    a.incoerenze.length +
    a.senzaAppello.length
  );
}

export type ChiavePasso = 'rosa' | 'impostazioni' | 'staff';

/**
 * I passi nell'ordine in cui si fanno, con il primo ancora da fare in
 * evidenza. La rosa e da fare per definizione: con la rosa i passi non ci
 * sono piu.
 */
export function elencoPassi(
  passi: Passi,
): { chiave: ChiavePasso; fatto: boolean; prossimo: boolean }[] {
  return segnaProssimo([
    { chiave: 'rosa' as const, fatto: false },
    { chiave: 'impostazioni' as const, fatto: passi.impostazioni },
    { chiave: 'staff' as const, fatto: passi.staff },
  ]);
}

/** Il saluto della Home, sull'ora italiana. */
export function saluto(adesso: Date): string {
  const ora = Number(oraLocale(adesso).slice(0, 2));
  if (ora >= 5 && ora < 13) return 'Buongiorno';
  if (ora >= 13 && ora < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

const GIORNO_MS = 24 * 60 * 60 * 1000;

/** Quanto manca a un impegno, in giorni di calendario italiani. */
export function quando(dataOra: string, adesso: Date): string {
  const giorni = Math.round(
    (Date.parse(dataLocale(dataOra)) - Date.parse(dataLocale(adesso))) / GIORNO_MS,
  );
  if (giorni <= 0) return 'Oggi';
  if (giorni === 1) return 'Domani';
  return `Tra ${giorni} giorni`;
}
